import { MAX_PRICE_IMPACT_THRESHOLD, MAX_SLIPPAGE_THRESHOLD, PUSD_TOKEN } from '@/constants/shared';
import { AmplitudeTrackingEvent, trackAmplitudeEvent } from '@/lib/amplitude';
import { Box, Button, Card, Flex, HStack, Icon, Text } from '@chakra-ui/react';
import { useAppKit } from '@reown/appkit/react';
import { SwapWidget, Token } from '@reservoir0x/relay-kit-ui';
import '@reservoir0x/relay-kit-ui/styles.css';
import { AdaptedWallet, adaptViemWallet, Execute } from '@reservoir0x/relay-sdk';
import { useEffect, useRef, useState } from 'react';
import { IoMdArrowForward } from 'react-icons/io';
import { LuBadgeAlert } from 'react-icons/lu';
import { useWalletClient } from 'wagmi';

const stablecoinSymbols = ['USDC', 'USDT', 'DAI', 'BUSD', 'TUSD', 'FRAX', 'LUSD', 'sUSD', 'USDP'];

function isElementVisible(element: Element): boolean {
  if (!element) return false;
  const rect = element.getBoundingClientRect();
  return (
    rect.width > 0 &&
    rect.height > 0 &&
    rect.bottom >= 0 &&
    rect.right >= 0 &&
    rect.top <= (window.innerHeight || document.documentElement.clientHeight) &&
    rect.left <= (window.innerWidth || document.documentElement.clientWidth)
  );
}

function extractValues(sectionId: string) {
  const parents = document.querySelectorAll(sectionId);
  let dollar: number | null = null;

  if (!parents.length) {
    return { dollar: null, input: null, rate: null };
  }

  const visibleParent = Array.from(parents).find((parent) => isElementVisible(parent));

  if (!visibleParent) {
    return { dollar: null, input: null, rate: null };
  }

  const divs = visibleParent.querySelectorAll('div');

  for (const div of divs) {
    if (div.textContent?.includes('$')) {
      const match = div.textContent?.match(/\$([0-9,.]+)/);
      if (match) {
        dollar = parseFloat(match[1].replace(/,/g, ''));
        break;
      }
    }
  }

  const inputEl = visibleParent.querySelector('input');
  const input = inputEl ? parseFloat(inputEl.value) : null;
  const rate = dollar !== null && input !== null && !isNaN(input) ? dollar / input : null;

  return { dollar, input, rate };
}

function extractGas() {
  const widgets = document.querySelectorAll('.relay-widget');
  const visibleWidget = Array.from(widgets).find((widget) => isElementVisible(widget));
  const gas = visibleWidget?.querySelector('.fa-gas-pump + div')?.textContent?.match(/\$([0-9,.]+)/);
  return gas ? parseFloat(gas[1].replace(/,/g, '')) : NaN;
}

const isStablecoin = (token?: Token) => {
  if (!token) return false;
  return stablecoinSymbols.includes(token.symbol);
};

const isPUSD = (token?: Token) => {
  if (!token) return false;
  return token.symbol === 'pUSD';
};

type QuoteValue = {
  dollar: number;
  input: number;
  rate: number;
};

type QuoteValues = {
  sell: QuoteValue;
  buy: QuoteValue;
  gas: number;
};

export const PlumeRelayBridge = ({
  onSwapSuccess,
  initialFromToken,
  initialToToken,
  lockChainId,
}: {
  onSwapSuccess?: () => void;
  initialFromToken?: Token;
  initialToToken?: Token;
  lockChainId?: number;
}) => {
  const { open } = useAppKit();
  const { data: walletClient } = useWalletClient();
  const [adaptedWallet, setAdaptedWallet] = useState<AdaptedWallet | null>(null);
  const widgetRef = useRef<HTMLDivElement>(null);
  const [values, setValues] = useState<QuoteValues>({
    sell: { dollar: NaN, input: NaN, rate: NaN },
    buy: { dollar: NaN, input: NaN, rate: NaN },
    gas: NaN,
  });
  const [slippage, setSlippage] = useState<number>(0);
  const [priceImpact, setPriceImpact] = useState<number>(0);

  useEffect(() => {
    setAdaptedWallet(walletClient ? adaptViemWallet(walletClient) : null);
  }, [walletClient]);

  const [fromToken, setFromToken] = useState<Token | undefined>(initialFromToken);
  const [toToken, setToToken] = useState<Token | undefined>(initialToToken);
  const shouldShowStablecoinMessage = isStablecoin(toToken) && !isPUSD(toToken);
  const shouldShowPUSDMessage = isPUSD(toToken) && slippage > MAX_SLIPPAGE_THRESHOLD;
  const shouldShowStargateMessage = priceImpact > MAX_PRICE_IMPACT_THRESHOLD;

  useEffect(() => {
    const sellDollar = Number(values.sell.dollar);
    const buyDollar = Number(values.buy.dollar);
    const slippage = ((sellDollar - buyDollar) / sellDollar) * 100;
    const sellRate = Number(values.sell.rate);
    const buyRate = Number(values.buy.rate);
    const priceImpact =
      sellRate > 0 && buyRate > 0 ? Math.abs((buyRate - sellRate) / sellRate) * 100 : Math.abs(slippage); // fallback

    const isValid =
      !isNaN(slippage) && !isNaN(priceImpact) && sellDollar > 0 && buyDollar > 0 && sellRate > 0 && buyRate > 0;

    if (isValid) {
      setSlippage(Number(slippage.toFixed(2)));
      setPriceImpact(Number(priceImpact.toFixed(2)));
    } else {
      setSlippage(0);
      setPriceImpact(0);
    }
  }, [values]);

  const handleSwitchToPUSD = () => {
    setToToken(PUSD_TOKEN);
  };

  const onSwapSuccessWithTracking = (e: Execute) => {
    const { details, steps } = e;
    trackAmplitudeEvent(AmplitudeTrackingEvent.PortalCompletedBridge, {
      from_asset: details?.currencyIn,
      to_asset: details?.currencyOut,
      from_asset_address: details?.currencyIn?.currency?.address,
      to_asset_address: details?.currencyOut?.currency?.address,
      amount_in_formatted: details?.currencyIn?.amountFormatted,
      tx_hash: steps?.[0]?.requestId,
      total_price_impact_usd: details?.totalImpact?.usd,
      total_price_impact_percent: details?.totalImpact?.percent,
      provider: 'relay',
    });
    onSwapSuccess?.();
  };

  return (
    <Card.Root rounded="18px" p={[4, 4, 6]} bg="bg" w="100%" maxW="max-content" mx="auto">
      <Card.Body p={0} w="full">
        <Box ref={widgetRef} className="relay-widget">
          {/* @ts-expect-error TODO: fix this */}
          <SwapWidget
            fromToken={fromToken}
            setFromToken={setFromToken}
            toToken={toToken}
            setToToken={setToToken}
            supportedWalletVMs={['evm']}
            wallet={adaptedWallet || undefined}
            onConnectWallet={open}
            lockChainId={lockChainId}
            singleChainMode={!!lockChainId}
            onAnalyticEvent={(event: string) => {
              if (event === 'QUOTE_RECEIVED') {
                // Let's give it a few ms for the widget to update
                setTimeout(() => {
                  const sell = extractValues('#from-token-section');
                  const buy = extractValues('#to-token-section');
                  const gas = extractGas();

                  setValues({ sell, buy, gas } as QuoteValues);
                }, 500);
              }
            }}
            onSwapSuccess={onSwapSuccessWithTracking}
          />
        </Box>
        {shouldShowStablecoinMessage && (
          <Flex gap={2} mt={2} bg="#FDF9F4" color="#8B4311" p={4} rounded="12px">
            <Icon as={LuBadgeAlert} boxSize={4} />
            <Box>
              <Text fontSize={14} fontWeight="500" lineHeight="18px" letterSpacing="-0.09px">
                {toToken?.symbol} isn't the official stablecoin on Plume
              </Text>
              <Text fontSize={14} fontWeight="500" lineHeight="18px" letterSpacing="-0.09px" color="#AC794F">
                Consider swapping to{' '}
                <Text as="span" color="#8B4311">
                  pUSD
                </Text>{' '}
                instead
              </Text>
              <Button
                variant="ghost"
                color="#09090B"
                fontSize={14}
                fontWeight="500"
                letterSpacing="-0.09px"
                p={0}
                onClick={handleSwitchToPUSD}
                _hover={{
                  bg: 'transparent',
                }}
              >
                Switch to pUSD
                <Icon as={IoMdArrowForward} boxSize={4} />
              </Button>
            </Box>
          </Flex>
        )}
        {shouldShowPUSDMessage && (
          <Flex gap={2} mt={2} bg="#FDF9F4" color="#8B4311" p={4} rounded="12px">
            <Icon as={LuBadgeAlert} boxSize={4} />
            <Box>
              <Text fontSize={14} fontWeight="500" lineHeight="18px" letterSpacing="-0.09px">
                High slippage detected
              </Text>
              <Text fontSize={14} fontWeight="500" lineHeight="18px" letterSpacing="-0.09px" color="#AC794F">
                Consider using{' '}
                <Text as="span" color="#8B4311">
                  pusd.plume.org
                </Text>{' '}
                for the best rates
              </Text>
              <Button
                variant="ghost"
                color="#09090B"
                fontSize={14}
                fontWeight="500"
                letterSpacing="-0.09px"
                p={0}
                onClick={() => window.open('https://pusd.plume.org', '_blank')}
                _hover={{
                  bg: 'transparent',
                }}
              >
                Official pUSD bridge
                <Icon as={IoMdArrowForward} boxSize={4} transform="rotate(-45deg)" />
              </Button>
            </Box>
          </Flex>
        )}
        {shouldShowStargateMessage && (
          <Flex gap={2} mt={2} bg="#FDF9F4" color="#8B4311" p={4} rounded="12px">
            <Icon as={LuBadgeAlert} boxSize={4} />
            <Box>
              <Text fontSize={14} fontWeight="500" lineHeight="18px" letterSpacing="-0.09px">
                Price impact is higher than usual
              </Text>
              <Text fontSize={14} fontWeight="500" lineHeight="18px" letterSpacing="-0.09px" color="#AC794F">
                Consider bridging via{' '}
                <Text as="span" color="#8B4311">
                  Stargate
                </Text>
              </Text>
              <Button
                variant="ghost"
                color="#09090B"
                fontSize={14}
                fontWeight="500"
                letterSpacing="-0.09px"
                p={0}
                onClick={() => window.open('https://stargate.finance/transfer', '_blank')}
                _hover={{
                  bg: 'transparent',
                }}
              >
                Stargate bridge
                <Icon as={IoMdArrowForward} boxSize={4} transform="rotate(-45deg)" />
              </Button>
            </Box>
          </Flex>
        )}
        <HStack gap={2} alignItems="center" mx="auto" mt={6}>
          <Text gap={2} fontSize="xs" fontWeight="500" lineHeight="18px" letterSpacing="-0.09px" color="text.secondary">
            Powered by
          </Text>
          <svg xmlns="http://www.w3.org/2000/svg" width="42" height="16" viewBox="0 0 42 16" fill="none">
            <path
              d="M38.2823 12.3462V11.9346L38.4195 10.7681H38.2994C37.9735 11.8659 36.9615 12.2262 35.6064 12.2262C33.9768 12.2262 32.7246 11.5229 32.7246 9.60172V3.44372C33.7195 3.35795 34.6972 3.35795 35.6921 3.44372V8.22947C35.6921 9.53311 36.1724 9.7218 36.8757 9.7218C37.7162 9.7218 38.0421 9.51596 38.2823 8.62399V3.44372C39.2771 3.35795 40.2549 3.35795 41.2498 3.44372V11.9689C41.2498 14.9021 39.6545 15.9999 36.3096 15.9999C35.452 15.9999 34.3542 15.9313 33.3078 15.794C33.2735 15.1251 33.3593 14.0959 33.5308 13.444C34.4056 13.5984 35.2461 13.667 36.0695 13.667C37.0815 13.667 38.2823 13.444 38.2823 12.3462Z"
              fill="#747474"
            />
            <path
              d="M28.2127 12.4322L28.0583 11.0942H27.9383C27.681 12.1406 26.6346 12.5522 25.2967 12.5522C23.6843 12.5522 22.4492 11.8318 22.4492 9.97926C22.4492 7.52635 23.77 7.01175 25.6397 7.01175C26.5145 7.01175 27.132 7.06321 27.9211 7.16613V6.77161C27.9211 5.96541 27.4065 5.50227 25.7083 5.50227C25.0565 5.50227 24.216 5.57088 23.1868 5.74242C23.101 5.03913 23.101 4.18147 23.1868 3.47819C24.4733 3.23805 25.5196 3.16943 26.6346 3.16943C29.4306 3.16943 30.8886 3.88987 30.8886 6.1884V12.4322C30.1853 12.5179 28.8988 12.5179 28.2127 12.4322ZM27.9211 9.6705V8.84715C27.4751 8.74423 26.9948 8.69277 26.6861 8.69277C25.8799 8.69277 25.4167 8.79569 25.4167 9.58474C25.4167 10.3395 25.8799 10.4252 26.4631 10.4252C27.1492 10.4252 27.6295 10.2194 27.9211 9.6705Z"
              fill="#747474"
            />
            <path
              d="M17.8027 12.3463V0.0645687C18.506 -0.0211974 20.067 -0.0211974 20.7702 0.0645687V12.3463C20.067 12.432 18.506 12.432 17.8027 12.3463Z"
              fill="#747474"
            />
            <path
              d="M15.945 6.65154V8.70992C14.8471 8.81284 12.2399 8.81284 10.8161 8.72707V8.91576C10.8161 9.97926 11.365 10.2194 12.8745 10.2194C13.8351 10.2194 14.5727 10.1165 15.5333 9.94495C15.619 10.6482 15.619 11.5402 15.5333 12.2263C14.2811 12.4493 13.3034 12.5522 12.1026 12.5522C9.5125 12.5522 7.84863 11.7975 7.84863 9.51612V6.23986C7.84863 3.85556 9.56396 3.16943 11.9654 3.16943C14.0753 3.16943 15.945 4.00994 15.945 6.65154ZM13.0632 7.06321V6.61723C13.0632 5.62234 12.5143 5.50227 11.9311 5.50227C11.365 5.50227 10.8161 5.62234 10.8161 6.60008V7.14898L11.8282 7.06321H13.0632Z"
              fill="#747474"
            />
            <path
              d="M0.75 12.3464V3.34097C1.64197 3.28951 2.51678 3.28951 3.40875 3.34097L3.58028 4.85045H3.70035C3.97481 3.66688 5.07261 3.16943 6.68501 3.16943C6.75363 3.87272 6.75363 5.02198 6.68501 5.70811C4.71239 5.70811 4.18064 6.10263 3.71751 7.33766V12.3464C3.01422 12.4322 1.45328 12.4322 0.75 12.3464Z"
              fill="#747474"
            />
          </svg>
        </HStack>
      </Card.Body>
    </Card.Root>
  );
};
